import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import { useCreateDriver, useCreateDriverAccess, useUpdateDriver } from './api';
import { driverFormSchema, type DriverFormInput } from './schemas';
import { BR_STATES, CNH_CATEGORIES, type Driver } from './types';
import { PRODUCT } from '@/config/product';

interface DriverFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driver?: Driver | null;
}

const DEFAULTS: DriverFormInput = {
  name: '',
  cpf: '',
  rg: '',
  cnh: '',
  cnhCategory: 'B',
  cnhExpiresAt: '',
  cnhFirstIssueAt: '',
  cnhPoints: 0,
  birthDate: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  notes: '',
  createUserAccount: false,
  accessPassword: '',
};

function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export function DriverFormDialog({ open, onOpenChange, driver }: DriverFormDialogProps) {
  const isEdit = !!driver;
  const createMutation = useCreateDriver();
  const updateMutation = useUpdateDriver(driver?.id ?? '');
  const accessMutation = useCreateDriverAccess(driver?.id ?? '');
  const jaTemAcesso = !!driver?.hasAppAccess;

  const form = useForm<DriverFormInput>({
    resolver: zodResolver(driverFormSchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (!open) return;
    if (driver) {
      form.reset({
        name: driver.name,
        cpf: driver.cpf,
        rg: driver.rg ?? '',
        cnh: driver.cnh,
        cnhCategory: driver.cnhCategory,
        cnhExpiresAt: isoToDateInput(driver.cnhExpiresAt),
        cnhFirstIssueAt: isoToDateInput(driver.cnhFirstIssueAt),
        cnhPoints: driver.cnhPoints,
        birthDate: isoToDateInput(driver.birthDate),
        phone: driver.phone ?? '',
        email: driver.email ?? '',
        address: driver.address ?? '',
        city: driver.city ?? '',
        state: driver.state ?? '',
        notes: driver.notes ?? '',
        createUserAccount: false,
        accessPassword: '',
      });
    } else {
      form.reset(DEFAULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, driver?.id]);

  const onSubmit = async (values: DriverFormInput) => {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(values);
        // O acesso e um endpoint separado: o PATCH do motorista nao cria login.
        // Vai depois do update para o email salvo ser o mesmo do login.
        if (values.createUserAccount && !jaTemAcesso) {
          await accessMutation.mutateAsync({
            email: values.email || undefined,
            password: values.accessPassword ?? '',
          });
          toast.success('Motorista atualizado e acesso ao aplicativo criado');
        } else {
          toast.success('Motorista atualizado');
        }
      } else {
        await createMutation.mutateAsync(values);
        toast.success('Motorista cadastrado');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar motorista' : 'Novo motorista'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize os dados pessoais e de CNH do motorista.'
              : 'Preencha os dados do motorista. CPF e número de CNH são validados.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Dados pessoais
            </p>
            <Field label="Nome completo *" error={form.formState.errors.name?.message}>
              <Input
                {...form.register('name')}
                placeholder="João da Silva"
                aria-invalid={!!form.formState.errors.name}
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="CPF *" error={form.formState.errors.cpf?.message}>
                <Input
                  {...form.register('cpf')}
                  placeholder="000.000.000-00"
                  aria-invalid={!!form.formState.errors.cpf}
                />
              </Field>
              <Field label="RG" error={form.formState.errors.rg?.message}>
                <Input {...form.register('rg')} />
              </Field>
              <Field label="Nascimento" error={form.formState.errors.birthDate?.message}>
                <Input type="date" {...form.register('birthDate')} />
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              CNH
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field
                label="Número CNH *"
                error={form.formState.errors.cnh?.message}
                className="sm:col-span-2"
              >
                <Input
                  {...form.register('cnh')}
                  placeholder="00000000000"
                  maxLength={11}
                  inputMode="numeric"
                  aria-invalid={!!form.formState.errors.cnh}
                />
              </Field>
              <Field label="Categoria *" error={form.formState.errors.cnhCategory?.message}>
                <Controller
                  control={form.control}
                  name="cnhCategory"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CNH_CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Primeira habilitação" error={form.formState.errors.cnhFirstIssueAt?.message}>
                <Input type="date" {...form.register('cnhFirstIssueAt')} />
              </Field>
              <Field
                label="Validade *"
                error={form.formState.errors.cnhExpiresAt?.message}
              >
                <Input
                  type="date"
                  {...form.register('cnhExpiresAt')}
                  aria-invalid={!!form.formState.errors.cnhExpiresAt}
                />
              </Field>
              <Field label="Pontos" error={form.formState.errors.cnhPoints?.message}>
                <Input
                  type="number"
                  min={0}
                  max={40}
                  {...form.register('cnhPoints', { valueAsNumber: true })}
                />
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Contato
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Telefone" error={form.formState.errors.phone?.message}>
                <Input {...form.register('phone')} placeholder="+55 11 99999-9999" />
              </Field>
              <Field label="E-mail" error={form.formState.errors.email?.message}>
                <Input type="email" {...form.register('email')} />
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Endereço
            </p>
            <Field label="Endereço" error={form.formState.errors.address?.message}>
              <Input {...form.register('address')} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field
                label="Cidade"
                error={form.formState.errors.city?.message}
                className="sm:col-span-2"
              >
                <Input {...form.register('city')} />
              </Field>
              <Field label="UF" error={form.formState.errors.state?.message}>
                <Controller
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <Select value={field.value || ''} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {BR_STATES.map((uf) => (
                          <SelectItem key={uf} value={uf}>
                            {uf}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Field>
            </div>
          </section>

          {/* Acesso ao app: vale no cadastro E na edicao. Antes so aparecia no
              cadastro, entao quem salvava sem marcar nao tinha como criar
              depois -- era preciso apagar o motorista e cadastrar de novo. */}
          {jaTemAcesso ? (
            <section className="space-y-1 rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-sm font-medium">Acesso ao aplicativo ativo</p>
              <p className="text-xs text-muted-foreground">
                O motorista entra no app {PRODUCT.name} com{' '}
                <span className="font-medium text-foreground">{driver?.email}</span>. A senha
                ele troca na tela de Perfil do próprio app.
              </p>
            </section>
          ) : (
            <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
              <div>
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 size-4 rounded border-border"
                    {...form.register('createUserAccount')}
                  />
                  <div>
                    <p className="text-sm font-medium">Criar acesso ao aplicativo</p>
                    <p className="text-xs text-muted-foreground">
                      O motorista poderá fazer login no app {PRODUCT.name} para executar checklists
                      e registrar abastecimentos.
                    </p>
                  </div>
                </label>
              </div>

              {form.watch('createUserAccount') && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field
                    label="Senha inicial *"
                    error={form.formState.errors.accessPassword?.message}
                  >
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="mínimo 10 caracteres"
                      {...form.register('accessPassword')}
                    />
                  </Field>
                  <div className="self-end pb-1 text-xs text-muted-foreground">
                    O motorista usa o e-mail acima + esta senha. Ele pode trocar depois
                    na própria tela de Perfil do app.
                  </div>
                </div>
              )}
            </section>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Salvar alterações' : 'Cadastrar motorista'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
